import { createContext, useContext } from "react";
import { loadLocale, MessageInfo } from "../../locale/loadLocale.ts";
import {
  FinAid,
  getFinAid,
  User,
  userInfo,
} from "../../common/api/decimal/methods/user.ts";
import { DecimalResultData } from "../../common/api/decimal/utils.ts";
import { getBridgeUser } from "../../common/api/bridge/methods.ts";
import { defer, json } from "react-router-dom";
import { isVstLocale } from "../../common/utils/locale.ts";
import { once } from "../../common/utils/function.ts";
import {
  SidebarLinks,
  CoursesContext,
  getCoursesContext,
} from "../../common/api/decimal/methods/course.ts";
import { usesContext } from "./my-courses/coursesUtils.ts";
import { loadBrandOnce } from "../root/rootLoader.ts";
import { getCart } from "../../common/api/decimal/methods/cart.ts";

export const homeRouteId = "home";

export type AugmentedExploreUser = DecimalResultData<typeof userInfo> &
  ExploreDataShape;

export type CoursesExtras = Pick<
  CoursesContext,
  "isInstructor" | "accessToken" | "defaultTermId"
> & {
  links: SidebarLinks[];
  coursesLink: string;
  trackingAndShippingLink: string;
  analyticsUrl: string | null;
  brand: CoursesContext["brand"];
};

interface CartSummary {
  itemCount: number;
  totalPrice: string;
  currency: string;
}

export interface HomeRouteLoaderShape {
  user: User;
  messageInfo: MessageInfo | undefined;
  coursesExtras: Promise<CoursesExtras>;
  finAid: Promise<FinAid>;
  cartSummary: Promise<CartSummary>;
}

export type ExploreDataShape = Pick<
  VST.Bridge.User,
  "hasAdmin" | "hasGroups" | "promotions"
> &
  Required<Pick<VST.Bridge.User, "creditList">> & {
    hasExplore: boolean;
    exploreBranding: VST.Bridge.User["branding"];
  };

export type HomeContextShape = Pick<
  HomeRouteLoaderShape,
  "messageInfo" | "coursesExtras" | "finAid" | "cartSummary"
> & {
  user: User &
    ExploreDataShape & {
      unseenExpiredCount: number;
    };
  isExploreReady: boolean;
};

export const HomeContext = createContext<HomeContextShape | undefined>(
  undefined
);

async function loadDecimalUser(): Promise<User> {
  const decimalUser = await userInfo();
  if (!decimalUser.ok) {
    throw json(
      { message: "loadDecimalUser not ok" },

      { status: decimalUser.status }
    );
  }
  if (!decimalUser.data) {
    throw json(
      { message: "loadDecimalUser no data" },
      { status: decimalUser.status }
    );
  }
  return decimalUser.data;
}

export async function loadExploreUser(): Promise<ExploreDataShape> {
  const bridgeUserOnError = {
    hasAdmin: false,
    hasGroups: false,
    creditList: [] as VST.Bridge.UserCredit[],
    promotions: [],
    branding: {},
  };
  const bridgeUser = await getBridgeUser()
    .then((response) => {
      if (response.status === 200) {
        return response;
      }
      throw new Error(`Bridge User status", ${response.status}`);
    })
    .catch(
      // Convert all throws to response objects to avoid complex catching
      // and error conversion logic. We don't care about the specific error,
      // but just want to turn off explore if an one occurs.
      () => ({
        status: 200,
        hasAssets: false,
        ...bridgeUserOnError,
      })
    );

  const hasExplore = bridgeUser.hasAssets;

  return {
    hasExplore: hasExplore,
    creditList: hasExplore ? bridgeUser.creditList ?? [] : [],
    hasAdmin: bridgeUser.hasAdmin,
    hasGroups: bridgeUser.hasGroups,
    promotions: bridgeUser.promotions,
    exploreBranding: bridgeUser.branding,
  };
}

export const getCoursesExtras = once(async (): Promise<CoursesExtras> => {
  const brand = await loadBrandOnce();
  // Avoid calling for the vast majority of users who don't have courses
  if (usesContext(brand)) {
    const context = await getCoursesContext();

    if (context.ok && context.data) {
      return {
        // There is a sense that the sidebar could be junked up by too many links so a limitation is set
        links: context.data.brand.sidebarLinks?.slice(0, 3) ?? [],
        isInstructor: context.data.isInstructor,
        coursesLink: context.data.brand.ea.akademos.coursesLink ?? "",
        trackingAndShippingLink:
          context.data.brand.ea.akademos.trackingAndShippingLink ?? "",
        analyticsUrl:
          context.data.links.find((link) => link.type === "vst_analytics")
            ?.link ?? null,
        brand: context.data.brand,
        accessToken: context.data.accessToken,
        defaultTermId: context.data.defaultTermId ?? "",
      };
    }
  }

  return {
    links: [],
    isInstructor: false,
    coursesLink: "",
    trackingAndShippingLink: "",
    analyticsUrl: null,
    brand: {} as CoursesContext["brand"],
    accessToken: "",
    defaultTermId: "",
  };
});

const getFinAidInfo = once(async () => {
  const brand = await loadBrandOnce();
  if (brand.VirtualStore.companyId && brand.VirtualStore.FinAid.enabled) {
    const result = await getFinAid();
    if (result.ok && result.data) {
      return result.data;
    }
  }
  return { balance: 0, currency: "USD" };
});

async function getCartSummary(): Promise<CartSummary> {
  const brand = await loadBrandOnce();
  if (brand.PDP.enabled && !!brand.VirtualStore.companyId) {
    const cart = await getCart();
    if (cart.ok && cart.data) {
      return {
        itemCount: cart.data.totalQuantity ?? 0,
        totalPrice: cart.data.subtotal.float,
        currency: cart.data.currencyLabel,
      };
    }
  }

  return {
    itemCount: 0,
    totalPrice: "0",
    currency: "USD",
  };
}

// HomeContextProvider depends on this being Promise<DeferredData>
// That will need revision if you change to Promise<HomeContextShape>
export async function loadHomeData() {
  const coursesExtras = getCoursesExtras();
  const finAid = getFinAidInfo();
  const cartSummary = getCartSummary();
  // Decimal throws all 401s (and only 401s) by default. Catch these errors
  // to avoid unhandled promise rejections. The user 401 will be caught by
  // the error boundary just fine, if applicable.
  Promise.all([coursesExtras, finAid, cartSummary]).catch(() => {});

  const user = await loadDecimalUser();

  let messageInfo = undefined;
  if (isVstLocale(user.locale)) {
    messageInfo = await loadLocale(user.locale);
  }

  const result: HomeRouteLoaderShape = {
    user,
    messageInfo,
    coursesExtras,
    finAid,
    cartSummary,
  };

  return defer(result as HomeRouteLoaderShape & Record<string, unknown>);
}

export function useHomeContextUser() {
  const ctx = useContext(HomeContext);
  return ctx!.user;
}

/**
 * In most cases you don't need this and can use the default hasExplore: false
 * for the initial render. This is if you need to know that the initial
 * bridge user data load is complete (e.g. loading candystripe)
 */
export function useHomeContextIsExploreReady(): boolean {
  const ctx = useContext(HomeContext);
  return ctx!.isExploreReady;
}

export function useCoursesExtras(): Promise<CoursesExtras> {
  const ctx = useContext(HomeContext);
  return ctx!.coursesExtras;
}

export function useFinAid(): Promise<FinAid> {
  const ctx = useContext(HomeContext);
  return ctx!.finAid;
}

export function useCartSummary(): Promise<CartSummary> {
  const ctx = useContext(HomeContext);
  return ctx!.cartSummary;
}
